import { cva, VariantProps } from 'class-variance-authority';

const buttonBaseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'px-6',
    'py-3',
    'data-[square=true]:!p-3',
    'data-[square=true]:aspect-square',
    'data-[square=true]:gap-0',
    'data-[square=true]:w-[46px]',
    'data-[size=sm]:!px-3',
    'data-[size=sm]:!py-1.5',
    'select-none',
    'font-semibold',
    'disabled:cursor-not-allowed',
    'transition-colors',
    'enabled:cursor-pointer',
    'relative',
    'box-border',
    'text-sm'
];

const buttonStylesSAG = cva(buttonBaseStyles, {
    variants: {
        color: {
            primary: [
                'text-white',
                'border',
                'border-[#007993]',
                'rounded-sm',
                'bg-[#007993]',
                'enabled:hover:bg-[#005159]',
                'enabled:hover:border-[#005159]',
                'enabled:active:bg-[#196269]',
                'dark:bg-[#00CCCC]',
                'dark:text-[#000028]',
                'dark:border-[#00CCCC]',
                'dark:hover:bg-[#00FFB9]',
                'dark:hover:border-[#00FFB9]',
                'dark:active:bg-[#00E5AA]',
                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:border-[#333353]',
                'disabled:bg-[#E5E5E9]',
                'disabled:border-[#E5E5E9]',
                'disabled:text-[#9999A9]'
            ],
            secondary: [
                'text-[#007993]',
                'border',
                'border-[#007993]',
                'bg-transparent',
                'rounded-sm',
                'enabled:hover:bg-[#D1FFF2]',
                'enabled:hover:border-[#005159]',
                'enabled:hover:text-[#005159]',
                'enabled:active:bg-transparent',
                'enabled:active:text-[#005159]',
                'enabled:active:border-[#005159]',
                'disabled:border-[#9999A9]',
                'disabled:text-[#9999A9]',
                'dark:border-[#00CCCC]',
                'dark:text-[#00CCCC]',
                'dark:enabled:hover:text-[#00FFB9]',
                'dark:enabled:hover:border-[#00FFB9]',
                'dark:enabled:hover:bg-[#001F39]',
                'dark:enabled:active:text-[#00FFB9]',
                'dark:enabled:active:border-[#00FFB9]',
                'dark:enabled:active:bg-transparent',
                'dark:disabled:text-[#737389]',
                'dark:disabled:border-[#737389]'
            ],
            tertiary: [
                'text-[#007993]',
                'border',
                'border-transparent',
                'bg-transparent',
                'enabled:hover:text-[#005159]',
                'enabled:hover:bg-[#D1FFF2]',
                'enabled:active:text-[#005159]',
                'enabled:active:bg-transparent',
                'disabled:bg-transparent',
                'disabled:text-[#9999A9]',
                'dark:text-[#00CCCC]',
                'dark:enabled:hover:text-[#00FFB9]',
                'dark:enabled:hover:bg-[#001F39]',
                'dark:enabled:active:bg-transparent',
                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-transparent'
            ],
            danger: [
                'text-white',
                'border',
                'border-[#D72339]',
                'rounded-sm',
                'bg-[#D72339]',
                'enabled:hover:bg-[#c81c31]',
                'enabled:hover:border-[#c81c31]',
                'enabled:active:bg-[#b7192c]',

                'dark:bg-[#FF2640]',
                'dark:text-[#000028]',
                'dark:border-[#FF2640]',
                'dark:hover:bg-[#d51e34]',
                'dark:hover:border-[#d51e34]',
                'dark:active:bg-[#b41a2d]',

                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:border-[#333353]',

                'disabled:bg-[#E5E5E9]',
                'disabled:border-[#E5E5E9]',
                'disabled:text-[#9999A9]'
            ]
        }
    },
    defaultVariants: {
        color: 'primary'
    }
});

const buttonStylesSHS = cva(buttonBaseStyles, {
    variants: {
        color: {
            primary: [
                'text-white',
                'rounded-full',
                'bg-[#cf4b00]',
                'enabled:hover:bg-[#ba4400]',
                'dark:disabled:bg-[#531e00]',
                'dark:disabled:text-[#666666]',
                'disabled:text-[#9999A9]',
                'disabled:bg-[#E5E5E9]'
            ],
            secondary: [
                'text-[#1A1A1A]',
                'rounded-full',
                'bg-white',
                'border',
                'border-[#b3b3b3]',
                'enabled:hover:bg-[#f2f2f2]',
                '[&>*]:enabled:text-[#cf4b00]',
                'disabled:text-[#9999A9]',
                'disabled:border-[#9999A9]',
                'dark:bg-[#1a1a1a]',
                'dark:border-[#4d4d4d]',
                'dark:text-[#e6e6e6]',
                '[&>*]:dark:enabled:text-[#e6e6e6]',
                'dark:hover:bg-[#313131]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:text-[#737389]'
            ],
            tertiary: [
                'text-[#1A1A1A]',
                'rounded-full',
                'bg-transparent',
                '[&>*]:enabled:text-[#cf4b00]',
                'enabled:hover:bg-[#e6e6e6]',
                'disabled:text-[#9999A9]',
                'dark:text-[#e6e6e6]',
                '[&>*]:dark:enabled:text-[#e6e6e6]',
                'dark:hover:bg-[#313131]',
                'dark:disabled:text-[#737389]'
            ],
            danger: [
                'text-white',
                'rounded-full',
                'bg-[#cf4b00]',
                'enabled:hover:bg-[#ba4400]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:text-[#737389]',
                'disabled:text-[#9999A9]',
                'disabled:bg-[#E5E5E9]'
            ]
        }
    },
    defaultVariants: {
        color: 'primary'
    }
});

export { buttonStylesSAG, buttonStylesSHS };

export type ButtonProps = VariantProps<typeof buttonStylesSAG | typeof buttonStylesSHS>;
